import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useKeycloak } from "@react-keycloak/web";
import { getData, postDataWithBody, putDataWithBody, postDataWithFiles } from "../../../Services/Services";
import api from "../../../Services/api";
import {
  createFleetUser, getAllFleetUsers, updateFleetUser, deleteFleetUser, validateUserNames,
  getThemeURL, getRegionByRole, getAllRole,
  keycloakbaseurl, getUserDataByEmailAttr, updateUserByUserId, createUserintoRealm,
} from "../../../Services/EndPoints";
import HierarchyDropdown from "../../../Components/UiComponents/HierarchyDropdown/HierarchyDropdown";
import "react-dropdown-tree-select/dist/styles.css";
import "../Common/Form/HierarchySelect/HierarchySelect.css";
import "./FleetUserManagement.css";
import { ListPager } from "../Common/ListCard/ListCard";

// ─── Constants ────────────────────────────────────────────────────────────────
// All toasts on this page show at the top center
const TOAST_TOP = { position: toast.POSITION.TOP_CENTER, autoClose: 3000 };
const notify = {
  success: (msg, opts) => toast.success(msg, { ...TOAST_TOP, ...opts }),
  error: (msg, opts) => toast.error(msg, { ...TOAST_TOP, ...opts }),
};
// eslint-disable-next-line no-unused-vars -- Shift field is hidden for now
const SHIFTS = [
  { value: "MORNING", label: "Morning (06:00 - 14:00)" },
  { value: "EVENING", label: "Evening (14:00 - 22:00)" },
  { value: "NIGHT", label: "Night (22:00 - 06:00)" },
  { value: "GENERAL", label: "General (09:00 - 18:00)" },
];
const LICENSE_TYPES = [
  { value: "LMV", label: "LMV - Light Motor Vehicle" },
  { value: "HMV", label: "HMV - Heavy Motor Vehicle" },
  { value: "HGMV", label: "HGMV - Heavy Goods Motor Vehicle" },
  { value: "HPMV", label: "HPMV - Heavy Passenger Motor Vehicle" },
  { value: "TRANS", label: "Transport" },
];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_ADDRESS_LENGTH = 250;

// Only newly-picked images hold a local blob: URL that needs releasing
const revokeNewImageUrls = (items) => {
  items.forEach((item) => { if (!item.isExisting && item.url) URL.revokeObjectURL(item.url); });
};

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  employeeId: "",
  userName: "",
  phone: "",
  secondaryPhone: "",
  role: "",
  shift: "",
  licenseNumber: "",
  licenseType: "",
  licenseExpiry: "",
  regions: [],
  bloodGroup: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  address: "",
  status: true,
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const sectionCard = {
  background: "#fff",
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  marginBottom: 20,
  overflow: "hidden",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
};
const sectionHeader = (color1, color2) => ({
  background: `linear-gradient(135deg, ${color1}, ${color2})`,
  padding: "12px 18px",
  display: "flex",
  alignItems: "center",
  gap: 10,
  borderRadius: "12px 12px 0 0", // keeps rounded corners on cards that allow overflow
});
const sectionBody = { padding: "18px 20px" };
const fieldGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "14px 20px" };
const label = { fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 5, display: "block" };
const inputStyle = {
  width: "100%", border: "1px solid #e2e8f0", borderRadius: 7,
  padding: "8px 11px", fontSize: 13, color: "#1e293b", outline: "none",
  background: "#f8fafc", boxSizing: "border-box",
  transition: "border-color 0.15s",
};
const requiredStar = { color: "#ef4444", marginLeft: 2 };
const td = { padding: "10px 14px", color: "#475569" };

// ─── Sub-components ───────────────────────────────────────────────────────────
const Field = ({ label: lbl, required, error, children }) => (
  <div>
    <label style={label}>{lbl}{required && <span style={requiredStar}>*</span>}</label>
    {children}
    {error && <div style={{ color: "#ef4444", fontSize: 11, marginTop: 4 }}>{error}</div>}
  </div>
);

const Input = ({ value, onChange, placeholder, type = "text", readOnly, error, maxLength, inputMode }) => (
  <input
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    readOnly={readOnly}
    maxLength={maxLength}
    inputMode={inputMode}
    style={{
      ...inputStyle,
      background: readOnly ? "#f1f5f9" : "#f8fafc",
      cursor: readOnly ? "not-allowed" : "text",
      borderColor: error ? "#ef4444" : "#e2e8f0",
    }}
    onFocus={e => !readOnly && (e.target.style.borderColor = error ? "#ef4444" : "#1a7a73")}
    onBlur={e => (e.target.style.borderColor = error ? "#ef4444" : "#e2e8f0")}
  />
);

const Select = ({ value, onChange, children, error, disabled }) => (
  <select
    value={value}
    onChange={onChange}
    disabled={disabled}
    style={{ ...inputStyle, cursor: disabled ? "not-allowed" : "pointer", background: disabled ? "#f1f5f9" : "#f8fafc", borderColor: error ? "#ef4444" : "#e2e8f0" }}
    onFocus={e => (e.target.style.borderColor = error ? "#ef4444" : "#1a7a73")}
    onBlur={e => (e.target.style.borderColor = error ? "#ef4444" : "#e2e8f0")}
  >
    {children}
  </select>
);

// On/off switch (green = on) with a label that follows the state
const ToggleSwitch = ({ checked, onChange, onLabel = "On", offLabel = "Off" }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", border: "none", background: "none", cursor: "pointer" }}
  >
    <span style={{
      position: "relative", width: 42, height: 24, borderRadius: 999, flexShrink: 0,
      background: checked ? "#16a34a" : "#cbd5e1", transition: "background 0.2s",
    }}>
      <span style={{
        position: "absolute", top: 3, left: checked ? 21 : 3, width: 18, height: 18, borderRadius: "50%",
        background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.25)", transition: "left 0.2s",
      }} />
    </span>
    <span style={{ fontSize: 13, fontWeight: 600, color: checked ? "#16a34a" : "#64748b" }}>
      {checked ? onLabel : offLabel}
    </span>
  </button>
);

const SectionTitle = ({ title, color1 = "#0f4a45", color2 = "#1a7a73" }) => (
  <div style={sectionHeader(color1, color2)}>
    <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>{title}</span>
  </div>
);

// Letters and single spaces only (e.g. "Ravi Kumar")
const isValidName = (name) => /^[a-zA-Z]+(?: [a-zA-Z]+)*$/.test(name.trim());
const isValidPhone = (p) => /^[6-9]\d{9}$/.test(p.trim());
// Indian DL: state (2 letters) + RTO code (2 or 3 digits) + year (4) + number (7)
// e.g. BR0120231234567 (15) or AP42620250000099 (16, RTO 426)
const isValidLicense = (l) => /^[A-Z]{2}[0-9]{2,3}(19|20)[0-9]{2}[0-9]{7}$/.test(l.replace(/[\s-]/g, "").toUpperCase());

// Email is not entered — it's built from the User ID, like the bank Create User form:
// lowercase user id + "@" + the logged-in user's email domain (from the Keycloak token)
const DEFAULT_EMAIL_DOMAIN = "bscb.com";
const emailDomainFromToken = (tokenParsed) => {
  const email = tokenParsed?.email || "";
  const at = email.lastIndexOf("@");
  return at > -1 && at < email.length - 1 ? email.slice(at + 1).toLowerCase() : DEFAULT_EMAIL_DOMAIN;
};
const toUserEmail = (userName, domain) => `${(userName || "").trim().toLowerCase()}@${domain}`;

// ── Row helpers for /user/getallByAppType ──
const userRole = (u) => u.user_role_map?.[0]?.roleEntity || null;
const userRoleName = (u) => userRole(u)?.name || "";
const userRoleId = (u) => u.user_role_map?.[0]?.role_id ?? userRole(u)?.id ?? null;
const userFullName = (u) => [u.first_name, u.middle_name, u.last_name].filter(Boolean).join(" ");
const isDriverRow = (u) => u.is_driver === true || userRoleName(u).trim().toLowerCase() === "driver";
const pad2 = (n) => String(n).padStart(2, "0");
// Accepts epoch ms (number or numeric string) or a date string; returns a Date or null
const toDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const d = new Date(/^\d+$/.test(String(value)) ? Number(value) : value);
  return isNaN(d.getTime()) ? null : d;
};
// "08-10-2026, 03:20 PM"
const formatDateTime = (value) => {
  const d = toDate(value);
  if (!d) return "";
  const h = d.getHours();
  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}, ${pad2(h % 12 || 12)}:${pad2(d.getMinutes())} ${h < 12 ? "AM" : "PM"}`;
};
// "30-10-2026"
const formatDate = (value) => {
  const d = toDate(value);
  return d ? `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}` : "";
};
// "2026-10-30" for <input type="date">
const toInputDate = (value) => {
  const d = toDate(value);
  return d ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` : "";
};

// Backend list rows are snake_case; map them back to form fields for editing
const rowToForm = (u) => ({
  firstName: u.first_name || "",
  lastName: u.last_name || "",
  employeeId: u.employee_id || "",
  userName: u.user_name || "",
  phone: u.phone_number || "",
  secondaryPhone: u.secondary_phone_number || "",
  role: userRoleId(u) != null ? String(userRoleId(u)) : "",
  shift: u.shift || "",
  licenseNumber: u.license_number || "",
  licenseType: u.license_type || "",
  licenseExpiry: toInputDate(u.license_expiry), // API may send epoch ms; the date input needs YYYY-MM-DD
  regions: (u.user_region_map || []).map((r) => String(r.region_id)),
  bloodGroup: u.blood_group || "",
  emergencyContactName: u.emergency_contact_name || "",
  emergencyContactPhone: u.emergency_contact_phone || "",
  address: u.address || "",
  status: u.status !== false,
});

const userPhotoUrl = (u) => u.attachments_path || (u.image_paths || [])[0] || null;

// Round photo; falls back to the first letter of the name when there's no photo or it fails to load
const UserAvatar = ({ user, size = 34, light = false }) => {
  const [failed, setFailed] = useState(false);
  const url = userPhotoUrl(user);
  const box = { width: size, height: size, borderRadius: "50%", flexShrink: 0 };
  if (url && !failed) {
    return (
      <img src={url} alt="" onError={() => setFailed(true)}
        style={{ ...box, objectFit: "cover", border: light ? "2px solid rgba(255,255,255,0.5)" : "1px solid #e2e8f0" }} />
    );
  }
  return (
    <div style={{
      ...box, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: Math.round(size * 0.4),
      background: light ? "rgba(255,255,255,0.2)" : "#e6f4f2", color: light ? "#fff" : "#0f4a45",
    }}>
      {(user.first_name || "?").charAt(0).toUpperCase()}
    </div>
  );
};

// Read-only label/value pair for the details popup
const DetailItem = ({ label: lbl, value, wide }) => (
  <div style={{ gridColumn: wide ? "1 / -1" : undefined, minWidth: 0 }}>
    <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>{lbl}</div>
    <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 500, wordBreak: "break-word" }}>
      {value === 0 || value === false ? String(value) : value || "NA"}
    </div>
  </div>
);

const DetailSection = ({ title, children }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ fontSize: 12, fontWeight: 700, color: "#0f4a45", borderBottom: "1px solid #e2e8f0", paddingBottom: 6, marginBottom: 12 }}>{title}</div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: "12px 18px" }}>{children}</div>
  </div>
);

// Full details of one user, shown in place of the list (not a popup)
const UserDetailsView = ({ user, regionNames, onBack, onEdit }) => {
  const role = userRole(user);
  const active = user.status !== false;
  const isDriver = isDriverRow(user);
  // Prefer the name sent with the user; fall back to the region tree, then the id
  const regions = (user.user_region_map || []).map((r) => r.region_name || regionNames[String(r.region_id)] || `Region ${r.region_id}`);
  const photoUrl = userPhotoUrl(user);
  const licenseUrl = user.license_file_path || null;

  return (
    <div>
      <button type="button" onClick={onBack}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 14, padding: "7px 14px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", color: "#0f4a45", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        Back to list
      </button>
      <div style={sectionCard}>
        {/* Header */}
        <div style={{ ...sectionHeader("#0f4a45", "#1a7a73"), justifyContent: "space-between", padding: "14px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <UserAvatar user={user} size={42} light />
            <div>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>{userFullName(user) || "—"}</div>
              <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>{role?.name || "No role"} · {user.user_name}</div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "18px 22px" }}>
          <DetailSection title="Personal Details">
            <DetailItem label="First Name" value={user.first_name} />
            <DetailItem label="Last Name" value={user.last_name} />
            <DetailItem label="Employee ID" value={user.employee_id} />
            <DetailItem label="Primary Phone" value={user.phone_number} />
            <DetailItem label="Secondary Phone" value={user.secondary_phone_number} />
            <DetailItem label="Blood Group" value={user.blood_group} />
          </DetailSection>

          <DetailSection title="Role & Access">
            <DetailItem label="Role" value={role?.name} />
            <DetailItem label="Role Category" value={role ? (role.is_admin ? "Admin" : "User") : ""} />
            <DetailItem label="User ID (Login)" value={user.user_name} />
            <DetailItem label="Status" value={
              <span style={{ background: active ? "#dcfce7" : "#f1f5f9", color: active ? "#16a34a" : "#64748b", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
                {active ? "Active" : "Inactive"}
              </span>
            } />
            <DetailItem label={`Regions (${regions.length})`} wide value={
              regions.length ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 110, overflowY: "auto" }}>
                  {regions.map((r, i) => (
                    <span key={i} style={{ background: "#f0fdfb", color: "#0f4a45", border: "1px solid #ccfbf1", borderRadius: 6, padding: "2px 8px", fontSize: 11.5, fontWeight: 600 }}>{r}</span>
                  ))}
                </div>
              ) : ""
            } />
          </DetailSection>

          {isDriver && (
            <DetailSection title="Driver Details">
              <DetailItem label="License Number" value={user.license_number} />
              <DetailItem label="License Type" value={user.license_type} />
              <DetailItem label="License Expiry" value={formatDate(user.license_expiry)} />
              <DetailItem label="License Photo" value={
                licenseUrl ? <a href={licenseUrl} target="_blank" rel="noreferrer"><img src={licenseUrl} alt="License" style={{ height: 70, borderRadius: 6, border: "1px solid #e2e8f0" }} /></a> : ""
              } />
            </DetailSection>
          )}

          <DetailSection title="Emergency Contact & Address">
            <DetailItem label="Emergency Contact Name" value={user.emergency_contact_name} />
            <DetailItem label="Emergency Contact Phone" value={user.emergency_contact_phone} />
            <DetailItem label="Address" wide value={user.address} />
          </DetailSection>

          <DetailSection title="Attachments">
            <DetailItem label="Photo" value={
              photoUrl ? <a href={photoUrl} target="_blank" rel="noreferrer"><img src={photoUrl} alt={`${userFullName(user) || "User"} profile`} style={{ height: 80, borderRadius: 6, border: "1px solid #e2e8f0" }} /></a> : ""
            } />
          </DetailSection>

          <DetailSection title="Record">
            <DetailItem label="Created On" value={formatDateTime(user.createdOn)} />
            <DetailItem label="Updated On" value={formatDateTime(user.updatedOn)} />
          </DetailSection>
        </div>

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "12px 20px", borderTop: "1px solid #f1f5f9" }}>
          <button type="button" onClick={onBack}
            style={{ padding: "8px 20px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Back</button>
          <button type="button" onClick={onEdit}
            style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #0f4a45, #1a7a73)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Edit User</button>
        </div>
      </div>
    </div>
  );
};

const ImageAttachments = ({ items, onAddFiles, onRemoveItem, error }) => {
  const [preview, setPreview] = useState(null);
  return (
    <div>
      <label
        style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: 6, padding: "18px 14px", borderRadius: 9, cursor: "pointer",
          border: `1.5px dashed ${error ? "#ef4444" : "#cbd5e1"}`, background: "#f8fafc",
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
        </svg>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
          {items.length > 0 ? "Click to replace image" : "Click to upload image"}
        </span>
        <span style={{ fontSize: 10.5, color: "#94a3b8" }}>JPG, PNG, WEBP · 1 image only · Max 5MB</span>
        <input type="file" accept="image/*" onChange={onAddFiles} style={{ display: "none" }} />
      </label>

      {items.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 10, marginTop: 10 }}>
          {items.map((item) => (
            <div key={item.id} style={{ position: "relative", minWidth: 0, borderRadius: 8, overflow: "hidden", border: "1px solid #e2e8f0", background: "#e2e8f0" }}>
              <img
                src={item.url} alt={item.name}
                onClick={() => setPreview(item)}
                style={{ width: "100%", height: 80, objectFit: "cover", display: "block", cursor: "pointer" }}
              />
              <button
                type="button"
                onClick={() => onRemoveItem(item.id)}
                title="Remove"
                style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", border: "none", background: "rgba(239,68,68,0.9)", color: "#fff", cursor: "pointer", fontSize: 12, lineHeight: 1 }}
              >×</button>
              <div style={{ fontSize: 9, color: "#64748b", background: "#f1f5f9", padding: "3px 5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.name}
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <div style={{ color: "#ef4444", fontSize: 11, marginTop: 6 }}>{error}</div>}

      {preview && (
        <div
          onClick={() => setPreview(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.82)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
        >
          <img
            src={preview.url} alt={preview.name}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "90vw", maxHeight: "85vh", borderRadius: 8, objectFit: "contain" }}
          />
        </div>
      )}
    </div>
  );
};

// Region tree — same source and rules as the bank Create User form (UserRegistrationForm.js)
// "SUPER ADMIN" (bank) or a fleet super admin role such as "PRUMOBILITY SUPER ADMIN"
const isSuperAdminProfile = (profile) =>
  profile.role_name === "SUPER ADMIN" ||
  (String(profile.app_type || "").toUpperCase() === "FLEET" &&
    String(profile.role_name || "").toUpperCase().includes("SUPER ADMIN"));

// Payload for the old POST region call — not used while regions load via GET getRegionByRole.
// Uncomment together with the commented postDataWithBody(getRegionByRole, …) call to switch back.
// const createRegionPayload = (profile) => {
//   let admin_role = "U";
//   if (isSuperAdminProfile(profile)) admin_role = "SA";
//   else if (profile.is_admin === true) admin_role = "HO";
//   return { admin_role, userId: profile.id || null };
// };

const flatToRegionTree = (flat, maxDepth) => {
  const roots = [];
  const all = new Map();
  flat.forEach((item) => {
    all.set(item.id, {
      ...item,
      id: String(item.id),
      label: item.name === "Bihar" ? "All districts" : item.name,
      value: String(item.id),
      depth: 1,
      children: [],
    });
  });
  for (const item of all.values()) {
    const parentId = item.parent_region_id;
    if (!parentId || !all.has(parentId) || String(parentId) === item.id) {
      item.expanded = true; // open the top level (e.g. BIHAR) like the bank form
      roots.push(item);
      continue;
    }
    const parent = all.get(parentId);
    item.depth = (parent.depth || 1) + 1;
    if (item.depth <= maxDepth) parent.children.push(item);
    else roots.push(item);
  }
  return roots;
};

// Returns a fresh copy of the tree with `checked` set for the given region ids
// Ids of the given nodes plus every region under them (the tree dropdown reports
// only the parent when all its children are ticked)
const expandRegionIds = (tree, selectedIds) => {
  const wanted = new Set(selectedIds.map(String));
  const out = new Set();
  const addAll = (n) => { out.add(String(n.id)); (n.children || []).forEach(addAll); };
  const walk = (nodes) => (nodes || []).forEach((n) => {
    if (wanted.has(String(n.id))) addAll(n);
    else walk(n.children);
  });
  walk(tree);
  // keep any selected id not found in the tree
  wanted.forEach((id) => out.add(id));
  return [...out];
};

const markCheckedRegions = (nodes, ids) =>
  nodes.map((n) => ({
    ...n,
    checked: ids.includes(n.id),
    children: n.children ? markCheckedRegions(n.children, ids) : n.children,
  }));

// One image per upload box (max 5MB); a new pick replaces the current one.
// Items are either existing (isExisting:true, remote URL) or newly picked (local blob URL + File).
const toExistingImage = (url, key) => {
  let name = url.split("/").pop() || url;
  try { name = decodeURIComponent(name); } catch { /* keep raw name */ }
  return { id: `existing-${key}`, isExisting: true, url, name };
};

const useSingleImage = () => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);
  useEffect(() => () => revokeNewImageUrls(itemsRef.current), []); // revoke on unmount

  const onAddFiles = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError(`${file.name}: only image files are allowed`);
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setError(`${file.name}: image exceeds 5MB`);
      return;
    }
    setError("");
    setItems((prev) => {
      revokeNewImageUrls(prev);
      return [{ id: `new-${Date.now()}`, isExisting: false, file, url: URL.createObjectURL(file), name: file.name }];
    });
  };

  const onRemoveItem = (id) => {
    setItems((prev) => {
      const target = prev.find((i) => i.id === id);
      if (target && !target.isExisting) URL.revokeObjectURL(target.url);
      return prev.filter((i) => i.id !== id);
    });
  };

  // Clear, or load an already-saved image (edit mode)
  const reset = (existingUrl = null, key = "img") => {
    setItems((prev) => {
      revokeNewImageUrls(prev);
      return existingUrl ? [toExistingImage(existingUrl, key)] : [];
    });
    setError("");
  };

  return { items, error, onAddFiles, onRemoveItem, reset };
};

// Adds only a newly picked image to the multipart body. A kept (already saved) image
// is not re-sent, and a removed one is signalled with removePhoto / removeLicenseFile.
const appendImage = (formData, fieldName, items) => {
  items.filter((item) => !item.isExisting).forEach((item) => formData.append(fieldName, item.file));
};

// True when an image saved on the user was removed in this edit (nothing left in the box)
const imageRemoved = (savedUrl, items) => Boolean(savedUrl) && items.length === 0;

// ─── Main Component ───────────────────────────────────────────────────────────
const FleetUserManagement = ({ initialTab = "form" }) => {
  const { keycloak } = useKeycloak();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [users, setUsers] = useState([]);
  const [regionTree, setRegionTree] = useState([]);        // tree from the API
  const [regionTreeData, setRegionTreeData] = useState([]); // tree passed to the dropdown (with checked flags)
  const [regionKey, setRegionKey] = useState(0);
  const [roles, setRoles] = useState([]);                  // from /role/getAllRoles
  const [appType, setAppType] = useState(null);            // logged-in user's app_type (e.g. "FLEET")
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editEmail, setEditEmail] = useState(""); // saved email of the user being edited (Keycloak lookup)
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  useEffect(() => { setPage(1); }, [searchText, pageSize]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewUser, setViewUser] = useState(null); // row shown in the details popup

  // Attachments — one image each
  const photo = useSingleImage();        // Attachments section
  const licensePhoto = useSingleImage(); // Driver Details section

  // Driver fields are always visible, but only mandatory for the Driver role
  const selectedRole = roles.find((r) => String(r.id) === String(form.role));
  const isDriver = (selectedRole?.name || "").trim().toLowerCase() === "driver";

  // Sidebar links point at separate routes — follow them when the route changes
  useEffect(() => { setActiveTab(initialTab); }, [initialTab]);

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };
  const setValue = (field) => (value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };
  // Names: letters and spaces only — drops other characters (also on paste),
  // no leading space and no double spaces
  const setName = (field, maxLen = 50) => (e) => {
    const value = e.target.value
      .replace(/[^a-zA-Z ]/g, "")
      .replace(/^ +/, "")
      .replace(/ {2,}/g, " ");
    set(field)({ target: { value: value.slice(0, maxLen) } });
  };
  const setClean = (field, pattern, maxLen, upper = false) => (e) => {
    let value = e.target.value.replace(pattern, "");
    if (upper) value = value.toUpperCase();
    set(field)({ target: { value: value.slice(0, maxLen) } });
  };


  const fetchUsers = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await getData(getAllFleetUsers);
      if (Array.isArray(res)) setUsers(res);
      else if (res?.status === "success") setUsers(res.data || []);
      else setUsers([]);
    } catch {
      setUsers([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "list") fetchUsers();
  }, [activeTab, fetchUsers]);

  useEffect(() => {
    (async () => {
      try {
        const profileRes = await getData(getThemeURL);
        const profile = profileRes?.[0];
        if (!profile) return;
        setAppType(profile.app_type || null);

        // Roles — same API and payload as the bank Create User form
        try {
          const roleList = await postDataWithBody(
            getAllRole,
            { name: profile.role_name, bankId: profile.bank_id || null },
            {}
          );
          setRoles(Array.isArray(roleList) ? roleList.filter((r) => r.is_deleted !== true) : []);
        } catch {
          notify.error("Failed to load roles");
        }

        // Regions
        try {
         // const list = await postDataWithBody(getRegionByRole, createRegionPayload(profile), {});
         const list = await getData(getRegionByRole);
          const maxDepth = isSuperAdminProfile(profile) ? Infinity : 3;
          const tree = flatToRegionTree(Array.isArray(list) ? list : [], maxDepth);
          setRegionTree(tree);
          setRegionTreeData(tree);
        } catch {
          notify.error("Failed to load regions");
        }
      } catch {
        notify.error("Failed to load user profile");
      }
    })();
  }, []);

  const handleRegionChange = (currentNode, selectedNodes) => {
    const ids = expandRegionIds(regionTree, selectedNodes.map((n) => n.id));
    setForm((f) => ({ ...f, regions: ids }));
    setErrors((prev) => ({ ...prev, regions: undefined }));
  };

  const validate = () => {
    const errs = {};
    const today = new Date(); today.setHours(0, 0, 0, 0);

    if (!form.firstName.trim()) errs.firstName = "First name is required";
    else if (!isValidName(form.firstName)) errs.firstName = "Only letters and spaces are allowed";
    if (form.lastName.trim() && !isValidName(form.lastName)) errs.lastName = "Only letters and spaces are allowed";

    if (!form.employeeId.trim()) errs.employeeId = "Employee ID is required";

    if (!editId) {
      if (!form.userName.trim()) errs.userName = "User ID is required";
      else if (!/^[a-zA-Z0-9]{3,8}$/.test(form.userName.trim())) errs.userName = "User ID must be 3–8 letters/numbers";
    }


    if (!form.phone.trim()) errs.phone = "Phone number is required";
    else if (!isValidPhone(form.phone)) errs.phone = "Enter a valid 10-digit mobile number";
    if (form.secondaryPhone && !isValidPhone(form.secondaryPhone)) errs.secondaryPhone = "Enter a valid 10-digit mobile number";

    if (!form.role) errs.role = "Role is required";
    if (form.regions.length === 0) errs.regions = "Region is required";

    // Driver Details are only shown (and checked) for the Driver role
    if (isDriver) {
      if (!form.licenseNumber.trim()) errs.licenseNumber = "License number is required for drivers";
      else if (!isValidLicense(form.licenseNumber)) errs.licenseNumber = "Enter a valid license number (e.g. BR0120231234567 or AP42620250000099)";
      if (!form.licenseType) errs.licenseType = "License type is required for drivers";
      if (!form.licenseExpiry) errs.licenseExpiry = "License expiry date is required for drivers";
      else if (new Date(form.licenseExpiry) < today) errs.licenseExpiry = "Driver license has already expired";
    }

    if (form.emergencyContactName.trim() && !isValidName(form.emergencyContactName))
      errs.emergencyContactName = "Only letters and spaces are allowed";
    if (form.emergencyContactPhone && !isValidPhone(form.emergencyContactPhone))
      errs.emergencyContactPhone = "Enter a valid 10-digit mobile number";

    if (form.address.length > MAX_ADDRESS_LENGTH)
      errs.address = `Address cannot exceed ${MAX_ADDRESS_LENGTH} characters`;

    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      notify.error("Please fix the highlighted errors before submitting");
      return false;
    }
    return true;
  };

  // Builds the multipart body: user JSON + photo (+ license photo for drivers)
  const buildFormData = async (jsonKey, isUpdate) => {
    const payload = {
      ...form,
      role: selectedRole?.name || "",
      roleId: form.role ? parseInt(form.role, 10) : null,
      user_role_map: form.role ? [{ role_id: parseInt(form.role, 10) }] : [],
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      emergencyContactName: form.emergencyContactName.trim(),
      userName: form.userName.trim(),
      isDriver,
      // Driver fields are sent only for drivers (they may hold stale values after a role change)
      licenseNumber: isDriver ? form.licenseNumber.replace(/[\s-]/g, "") : "",
      licenseType: isDriver ? form.licenseType : "",
      licenseExpiry: isDriver ? form.licenseExpiry : "",
      user_region_map: form.regions.map((id) => ({ region_id: parseInt(id, 10) })),
      appType, // from the logged-in user's profile (app_type)
      // e.g. "FleetAdmin" -> "fleetadmin@bscb.com"; on update keep the saved email (same as Keycloak)
      email: (isUpdate && editEmail) || toUserEmail(form.userName, emailDomainFromToken(keycloak?.tokenParsed)),
    };
    delete payload.regions;
    if (isUpdate) {
      // New image -> sent in files/licenseFile; removed -> flag; kept -> nothing
      const saved = users.find((u) => u.id === editId) || {};
      if (imageRemoved(userPhotoUrl(saved), photo.items)) payload.removePhoto = true;
      if (isDriver && imageRemoved(saved.license_file_path, licensePhoto.items)) payload.removeLicenseFile = true;
    }

    const formData = new FormData();
    formData.append(jsonKey, JSON.stringify(isUpdate ? { ...payload, id: editId } : payload));
    appendImage(formData, "files", photo.items);                      // user photo
    if (isDriver) appendImage(formData, "licenseFile", licensePhoto.items); // driving license photo
    return formData;
  };

  // GET /user/validateUserName — called with axios directly (same URL/token as getData) so we get
  // the real HTTP status and body even for non-200/error responses; getData would hide them as []
  const requestValidateUserName = async (userName) => {
    const url = "/" + process.env.REACT_APP_CORE_CONTEXT_PATH_NAME +
      validateUserNames.replace("USERNAME", encodeURIComponent(userName));
    try {
      const r = await axios.get(url, {
        withCredentials: true,
        headers: { Authorization: "Bearer " + localStorage.getItem("auth_token") },
      });
      return { httpStatus: r.status, data: r.data };
    } catch (e) {
      console.error("[FleetUser] validateUserName request failed:", e);
      return { httpStatus: e.response?.status, data: e.response?.data };
    }
  };

  // ── Keycloak (URM) update on edit — same flow as the bank Create User form ──
  const urmHeaders = () => ({
    "content-type": "application/json",
    Authorization: "Bearer " + keycloak?.token,
  });

  // GET {URM}/urm/admin/realms/{REALM}/users?email=…&exact=true → Keycloak user id
  const getKeycloakUserIdByEmail = async (email) => {
    const url = keycloakbaseurl + getUserDataByEmailAttr
      .replace("{REALM}", process.env.REACT_APP_URM_REALM)
      .replace("{EMAIL}", encodeURIComponent(email));
    const response = await api.getData(url, null, urmHeaders());
    return response?.data?.[0]?.id;
  };

  // PUT {URM}/urm/admin/realms/{REALM}/users/{id} with the edited name/phones/address
  const updateUserInKeycloak = async () => {
    const userName = form.userName.trim();
    // Look up by the saved email (falls back to the built one for older rows without email)
    const email = editEmail || toUserEmail(userName, emailDomainFromToken(keycloak?.tokenParsed));
    try {
      const keycloakUserId = await getKeycloakUserIdByEmail(email);
      if (!keycloakUserId) {
        notify.error(`Login account not found for ${email}. Unable to update the user.`);
        return false;
      }
      const url = keycloakbaseurl +
        updateUserByUserId.replace("{REALM}", process.env.REACT_APP_URM_REALM) + keycloakUserId;
      const body = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        emailVerified: true,
        enabled: form.status !== false,
        email,
        username: userName,
        attributes: {
          address: [form.address],
          phone1: [form.phone],
          phone2: [form.secondaryPhone],
        },
      };
      const response = await api.putData(url, urmHeaders(), JSON.stringify(body));
      if (response?.status === 204) return true;
      notify.error("Unable to update the user.");
      return false;
    } catch (e) {
      notify.error(e?.response?.data?.errorMessage || "Unable to update the user login.");
      return false;
    }
  };

  // POST {URM}/urm/admin/realms/{REALM}/users — creates the login with the default temporary password
  const createUserInKeycloak = async () => {
    const userName = form.userName.trim();
    const url = keycloakbaseurl + createUserintoRealm.replace("{REALM}", process.env.REACT_APP_URM_REALM);
    const body = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      emailVerified: true,
      enabled: form.status !== false,
      email: toUserEmail(userName, emailDomainFromToken(keycloak?.tokenParsed)),
      username: userName,
      attributes: {
        address: [form.address],
        phone1: [form.phone],
        phone2: [form.secondaryPhone],
      },
      credentials: [
        { type: "password", value: process.env.REACT_APP_NEW_USER_DEFAULT_PASSWORD, temporary: true },
      ],
    };
    try {
      const response = await api.postData(url, urmHeaders(), JSON.stringify(body));
      if (response?.status === 201) return true;
      notify.error("Unable to create the user login.");
      return false;
    } catch (e) {
      const msg = e?.response?.data?.errorMessage || "Failed to create the user login";
      notify.error(msg.includes("exists") ? `${msg}. Please use a different User ID.` : msg);
      return false;
    }
  };

  // Same as the bank form: find the new login, then map realm-management roles
  // (manage-users, manage-realm) using the REACT_APP_REAL_URM_* ids
  const assignKeycloakRealmRoles = async (userName) => {
    // …/realms/{REALM}/users (no trailing slash)
    const usersUrl = keycloakbaseurl + createUserintoRealm.replace("{REALM}", process.env.REACT_APP_URM_REALM);
    try {
      const found = await api.getData(usersUrl, { username: userName, exact: true }, urmHeaders());
      const keycloakUserId = found?.data?.[0]?.id;
      if (!keycloakUserId) return { success: false, error: "login not found" };
      await api.postData(
        `${usersUrl}/${keycloakUserId}/role-mappings/clients/${process.env.REACT_APP_REAL_URM_CLIENT_ID}`,
        urmHeaders(),
        JSON.stringify([
          { id: process.env.REACT_APP_REAL_URM_USERS, name: "manage-users" },
          { id: process.env.REACT_APP_REAL_URM_REALM, name: "manage-realm" },
        ])
      );
      return { success: true };
    } catch (e) {
      console.error("[FleetUser] Keycloak role mapping failed:", e);
      return { success: false, error: e?.response?.data?.errorMessage || e.message };
    }
  };

  // Some endpoints come back as a JSON string instead of an object — normalise it
  const asObject = (res) => {
    if (typeof res === "string") {
      try { return JSON.parse(res); } catch { return {}; }
    }
    return res || {};
  };
  const isSuccess = (res) => String(asObject(res).status || "").toLowerCase() === "success";

  const onSaved = (res, fallbackMsg) => {
    if (isSuccess(res)) {
      notify.success(asObject(res).msg || fallbackMsg);
      handleReset();
      setActiveTab("list");
    } else {
      notify.error(asObject(res).msg || "Failed to save user");
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      // ── Edit: update the Keycloak login first (same as the bank form), then the app user ──
      if (editId) {
        if (!(await updateUserInKeycloak())) return;
        const res = await putDataWithBody(updateFleetUser, await buildFormData("userData", true), {});
        onSaved(res, "User updated successfully");
        return;
      }

      // ── Create, step 1: GET /user/validateUserName?user_name=… ──
      const userName = form.userName.trim();
      const { httpStatus, data } = await requestValidateUserName(userName);
      const check = asObject(data);
      console.log("[FleetUser] validateUserName", userName, "→ HTTP", httpStatus, check);

      if (isSuccess(check)) {
        // ── step 2: status "success" → create the Keycloak login (same as the bank form) ──
        if (!(await createUserInKeycloak())) return;
        // ── step 3: give the login manage-users / manage-realm (a failure only warns) ──
        const roles = await assignKeycloakRealmRoles(userName);
        if (!roles.success) {
          notify.error(`User login created but role assignment failed: ${roles.error}`, { autoClose: 4000 });
        }
        // ── step 4: create the app user (POST /user/createV2) ──
        const res = await postDataWithFiles(createFleetUser, await buildFormData("userData", false));
        onSaved(res, "User created successfully");
      } else if (String(check.status || "").toLowerCase() === "failure") {
        // User ID already taken — stop
        const msg = check.msg || "User ID already exists. Please choose a different User ID.";
        setErrors((prev) => ({ ...prev, userName: msg }));
        notify.error(msg);
      } else {
        // No usable status in the response — show the HTTP code so the cause is visible
        notify.error(
          `Unable to validate the User ID${httpStatus ? ` (HTTP ${httpStatus})` : " (no response)"}. Please try again.`,
          { autoClose: 4000 }
        );
      }
    } catch (err) {
      console.error("[FleetUser] save failed:", err);
      notify.error("An error occurred while saving");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (u) => {
    setEditEmail(u.email || "");
    const editForm = rowToForm(u);
    setForm(editForm);
    setRegionTreeData(markCheckedRegions(regionTree, editForm.regions));
    setRegionKey((k) => k + 1);
    setErrors({});
    setEditId(u.id);
    photo.reset(u.attachments_path || (u.image_paths || [])[0] || null, `photo-${u.id}`);
    licensePhoto.reset(u.license_file_path || null, `license-${u.id}`);
    setActiveTab("form");
  };

  const handleDelete = async (id) => {
    try {
      const res = await putDataWithBody(deleteFleetUser, { id }, {});
      if (res?.status === "success") {
        notify.success(res.msg || "User deleted successfully");
        fetchUsers();
      } else {
        notify.error(res?.msg || "Failed to delete user");
      }
    } catch {
      notify.error("Error deleting user");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleReset = () => {
    setForm(EMPTY_FORM); setEditId(null); setEditEmail(""); setErrors({}); photo.reset(); licensePhoto.reset();
    setRegionTreeData(markCheckedRegions(regionTree, []));
    setRegionKey((k) => k + 1); // remount so the dropdown drops its internal selection
  };

  const regionNames = useMemo(() => {
    const names = {};
    const walk = (nodes) => (nodes || []).forEach((n) => { names[String(n.id)] = n.label; walk(n.children); });
    walk(regionTree);
    return names;
  }, [regionTree]);

  const q = searchText.toLowerCase();
  const filtered = users.filter(u =>
    !q ||
    userFullName(u).toLowerCase().includes(q) ||
    userRoleName(u).toLowerCase().includes(q) ||
    (u.employee_id || "").toLowerCase().includes(q) ||
    (u.user_name || "").toLowerCase().includes(q) ||
    u.phone_number?.includes(q)
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageRows = filtered.slice(pageStart, pageStart + pageSize);

  const handleDownload = () => {
    if (filtered.length === 0) {
      toast.info("No users to download");
      return;
    }
    const headers = ["S.No", "Name", "Emp ID", "User ID", "Role", "Phone Number", "Email", "Status"];
    const escape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = filtered.map((u, i) => [
      i + 1,
      userFullName(u),
      u.employee_id,
      u.user_name,
      userRoleName(u),
      u.phone_number,
      u.email,
      u.status !== false ? "Active" : "Inactive",
    ]);
    const csv = [headers, ...rows].map(r => r.map(escape).join(",")).join("\r\n");
    // BOM so Excel reads UTF-8 names correctly
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Fleet_Users_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const goToTab = (key) => {
    setViewUser(null); // leaving the details view
    setActiveTab(key);
  };

  const tabBtn = (key, text) => (
    <button
      onClick={() => goToTab(key)}
      style={{
        padding: "9px 22px", borderRadius: 8, border: "none", cursor: "pointer",
        fontSize: 13, fontWeight: 600,
        background: activeTab === key ? "linear-gradient(135deg, #0f4a45, #1a7a73)" : "#f1f5f9",
        color: activeTab === key ? "#fff" : "#64748b",
        boxShadow: activeTab === key ? "0 2px 8px rgba(26,122,115,0.3)" : "none",
        transition: "all 0.15s",
      }}
    >{text}</button>
  );

  return (
    <div style={{ padding: "16px 20px", fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
        {tabBtn("form", editId ? "✏ Edit User" : "+ Create User")}
        {tabBtn("list", "User List")}
        {editId && (
          <button
            onClick={handleReset}
            title="Discard edit and create a new user"
            style={{
              padding: "9px 18px", borderRadius: 8, border: "1.5px dashed #0f4a45",
              background: "#f0fdfb", color: "#0f4a45", fontSize: 13, fontWeight: 600,
              cursor: "pointer", marginLeft: 4,
            }}
          >+ New User</button>
        )}
        {activeTab === "list" && !viewUser && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto", flexWrap: "wrap" }}>
            <input
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search by name, emp ID, user ID, role, phone number..."
              title="Search by Name, Emp ID, User ID, Role or Phone Number"
              style={{ padding: "6px 12px", borderRadius: 7, border: "1px solid #cbd5e1", fontSize: 12, width: 260, maxWidth: "100%", outline: "none" }}
            />
            <button onClick={handleDownload} title="Download" disabled={listLoading}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 7, border: "none", background: "linear-gradient(135deg, #0f4a45, #1a7a73)", color: "#fff", fontSize: 12, fontWeight: 600, cursor: listLoading ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Download
            </button>
          </div>
        )}
      </div>

      {/* ── FORM TAB ── */}
      {activeTab === "form" && (
        <div>
          {/* Personal Details */}
          <div style={sectionCard}>
            <SectionTitle title="Personal Details" />
            <div style={sectionBody}>
              <div style={fieldGrid}>
                <Field label="First Name" required error={errors.firstName}>
                  <Input maxLength={50} value={form.firstName} onChange={setName("firstName")} placeholder="First name" error={errors.firstName} />
                </Field>
                <Field label="Last Name" error={errors.lastName}>
                  <Input maxLength={50} value={form.lastName} onChange={setName("lastName")} placeholder="Last name" error={errors.lastName} />
                </Field>
                <Field label="Employee ID" required error={errors.employeeId}>
                  <Input maxLength={20} value={form.employeeId} onChange={setClean("employeeId", /[^a-zA-Z0-9-]/g, 20, true)} placeholder="e.g. EMP-1023" readOnly={!!editId} error={errors.employeeId} />
                </Field>
                <Field label="Primary Phone Number" required error={errors.phone}>
                  <Input inputMode="numeric" maxLength={10} value={form.phone} onChange={setClean("phone", /\D/g, 10)} placeholder="10-digit mobile" error={errors.phone} />
                </Field>
                <Field label="Secondary Phone Number" error={errors.secondaryPhone}>
                  <Input inputMode="numeric" maxLength={10} value={form.secondaryPhone} onChange={setClean("secondaryPhone", /\D/g, 10)} placeholder="Alternate mobile" error={errors.secondaryPhone} />
                </Field>
                <Field label="Blood Group">
                  <Select value={form.bloodGroup} onChange={set("bloodGroup")}>
                    <option value="">Select</option>
                    {BLOOD_GROUPS.map(b => <option key={b} value={b}>{b}</option>)}
                  </Select>
                </Field>
              </div>
            </div>
          </div>

          {/* Role & Access — overflow visible so the Region dropdown list isn't clipped */}
          <div style={{ ...sectionCard, overflow: "visible", position: "relative", zIndex: 5 }}>
            <SectionTitle title="Role & Access" color2="#145c57" />
            <div style={sectionBody}>
              <div style={fieldGrid}>
                <Field label="Role" required error={errors.role}>
                  <Select value={form.role} onChange={set("role")} error={errors.role} disabled={!!editId}>
                    <option value="">Select a role</option>
                    {roles.map(r => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
                  </Select>
                </Field>
                <Field label="User ID (Login)" required={!editId} error={errors.userName}>
                  <Input maxLength={8} value={form.userName} onChange={setClean("userName", /[^a-zA-Z0-9]/g, 8)} placeholder="3–8 letters/numbers" readOnly={!!editId} error={errors.userName} />
                </Field>
                <Field label="Region" required error={errors.regions}>
                  <div className="input_text-form fleet-region-select" style={{ margin: 0 }}>
                    <HierarchyDropdown
                      key={regionKey}
                      data={regionTreeData}
                      onChange={handleRegionChange}
                      id="fleet-user-region"
                    />
                  </div>
                </Field>
                {/* Shift hidden for now — uncomment to show it again
                <Field label="Shift">
                  <Select value={form.shift} onChange={set("shift")}>
                    <option value="">Select a shift</option>
                    {SHIFTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </Select>
                </Field>
                */}
                <Field label="Status">
                  <ToggleSwitch
                    checked={form.status !== false}
                    onChange={setValue("status")}
                    onLabel="Active"
                    offLabel="Inactive"
                  />
                </Field>
              </div>
            </div>
          </div>

          {/* Driver Details — only for the Driver role */}
          {isDriver && (
          <div style={sectionCard}>
            <SectionTitle title="Driver Details" color1="#059669" color2="#047857" />
            <div style={sectionBody}>
              <div style={fieldGrid}>
                <Field label="Driving License Number" required={isDriver} error={errors.licenseNumber}>
                  <Input maxLength={16} value={form.licenseNumber} onChange={setClean("licenseNumber", /[^a-zA-Z0-9]/g, 16, true)} placeholder="e.g. BR0120231234567" error={errors.licenseNumber} />
                </Field>
                <Field label="License Type" required={isDriver} error={errors.licenseType}>
                  <Select value={form.licenseType} onChange={set("licenseType")} error={errors.licenseType}>
                    <option value="">Select type</option>
                    {LICENSE_TYPES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </Select>
                </Field>
                <Field label="License Expiry" required={isDriver} error={errors.licenseExpiry}>
                  <Input type="date" value={form.licenseExpiry} onChange={set("licenseExpiry")} error={errors.licenseExpiry} />
                </Field>
              </div>
              <div style={{ marginTop: 14, maxWidth: 640 }}>
                <Field label="Driving License Photo">
                  <ImageAttachments
                    items={licensePhoto.items}
                    onAddFiles={licensePhoto.onAddFiles}
                    onRemoveItem={licensePhoto.onRemoveItem}
                    error={licensePhoto.error}
                  />
                </Field>
              </div>
            </div>
          </div>
          )}

          {/* Emergency & Address */}
          <div style={sectionCard}>
            <SectionTitle title="Emergency Contact & Address" color2="#145c57" />
            <div style={sectionBody}>
              <div style={fieldGrid}>
                <Field label="Emergency Contact Name" error={errors.emergencyContactName}>
                  <Input maxLength={60} value={form.emergencyContactName} onChange={setName("emergencyContactName", 60)} placeholder="Contact person name" error={errors.emergencyContactName} />
                </Field>
                <Field label="Emergency Contact Phone" error={errors.emergencyContactPhone}>
                  <Input inputMode="numeric" maxLength={10} value={form.emergencyContactPhone} onChange={setClean("emergencyContactPhone", /\D/g, 10)} placeholder="10-digit mobile" error={errors.emergencyContactPhone} />
                </Field>
              </div>
              <div style={{ marginTop: 14 }}>
                <Field label="Address" error={errors.address}>
                  <textarea
                    value={form.address}
                    onChange={(e) => set("address")({ target: { value: e.target.value.slice(0, MAX_ADDRESS_LENGTH) } })}
                    maxLength={MAX_ADDRESS_LENGTH}
                    placeholder="Residential address"
                    rows={3}
                    style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6, borderColor: errors.address ? "#ef4444" : "#e2e8f0" }}
                  />
                  <div style={{ textAlign: "right", fontSize: 11, marginTop: 2, color: form.address.length >= MAX_ADDRESS_LENGTH ? "#ef4444" : "#94a3b8" }}>
                    {form.address.length}/{MAX_ADDRESS_LENGTH}
                  </div>
                </Field>
              </div>
            </div>
          </div>

          {/* Attachments */}
          <div style={sectionCard}>
            <SectionTitle title="Attachments" />
            <div style={sectionBody}>
              <div style={{ maxWidth: 640 }}>
                <Field label="Image (Photo)">
                  <ImageAttachments
                    items={photo.items}
                    onAddFiles={photo.onAddFiles}
                    onRemoveItem={photo.onRemoveItem}
                    error={photo.error}
                  />
                </Field>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 4 }}>
            <button
              onClick={handleReset}
              style={{ padding: "10px 24px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >Reset</button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                padding: "10px 28px", borderRadius: 8, border: "none",
                background: loading ? "#94a3b8" : "linear-gradient(135deg, #0f4a45, #1a7a73)",
                color: "#fff", fontSize: 13, fontWeight: 700,
                cursor: loading ? "not-allowed" : "pointer",
                boxShadow: "0 2px 8px rgba(26,122,115,0.35)",
              }}
            >
              {loading ? "Saving…" : editId ? "Update User" : "Create User"}
            </button>
          </div>
        </div>
      )}

      {/* ── USER DETAILS (replaces the list while open) ── */}
      {activeTab === "list" && viewUser && (
        <UserDetailsView
          user={viewUser}
          regionNames={regionNames}
          onBack={() => setViewUser(null)}
          onEdit={() => { const u = viewUser; setViewUser(null); handleEdit(u); }}
        />
      )}

      {/* ── LIST TAB ── */}
      {activeTab === "list" && !viewUser && (
        <div style={sectionCard}>
          <div style={{ overflowX: "auto" }}>
            {listLoading ? (
              <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>Loading users…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                No users found. <button onClick={() => goToTab("form")} style={{ color: "#0f4a45", background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Create one</button>
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "linear-gradient(135deg, #0f4a45, #1a7a73)" }}>
                    {["Photo", "Name", "Emp ID", "User ID", "Role", "Phone Number", "Status", "Actions"].map(h => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, color: "#fff", textTransform: "uppercase", letterSpacing: "0.4px", fontSize: 11, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((u, i) => {
                    const active = u.status !== false;
                    return (
                      <tr key={u.id || i} style={{ borderBottom: "1px solid #f1f5f9" }}
                        onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
                        onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                      >
                        <td style={{ ...td, width: 56 }}><UserAvatar user={u} size={34} /></td>
                        <td style={{ ...td, color: "#1e293b", fontWeight: 600 }}>{userFullName(u) || "—"}</td>
                        <td style={td}>{u.employee_id || "—"}</td>
                        <td style={td}>
                          <span style={{ background: "#f0fdfb", color: "#0f4a45", padding: "3px 9px", borderRadius: 6, fontWeight: 700 }}>{u.user_name || "—"}</span>
                        </td>
                        <td style={td}>{userRoleName(u) || "—"}</td>
                        <td style={td}>{u.phone_number || "—"}</td>
                        <td style={td}>
                          <span style={{ background: active ? "#dcfce7" : "#f1f5f9", color: active ? "#16a34a" : "#64748b", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
                            {active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td style={td}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button onClick={() => setViewUser(u)} title="View"
                              style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #dbeafe", background: "#eff6ff", color: "#2563eb", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                              </svg>
                            </button>
                            <button onClick={() => handleEdit(u)} title="Edit"
                              style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #ccfbf1", background: "#f0fdfb", color: "#0f4a45", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            <button onClick={() => setDeleteConfirm(u.id)} title="Delete"
                              style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #fecaca", background: "#fef2f2", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          {!listLoading && (
            <ListPager
              total={filtered.length}
              page={currentPage}
              pageSize={pageSize}
              onPage={setPage}
              onPageSize={setPageSize}
            />
          )}
        </div>
      )}

      {/* ── Saving loader (create / update) ── */}
      {loading && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(255,255,255,0.7)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <div className="spinner-border" role="status" style={{ width: 44, height: 44, color: "#1a7a73" }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#0f4a45" }}>
            {editId ? "Updating user…" : "Creating user…"}
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 14, padding: 28, maxWidth: 380, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>Delete User</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4, marginBottom: 18 }}>This action cannot be undone.</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ padding: "8px 20px", borderRadius: 7, border: "1.5px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} style={{ padding: "8px 20px", borderRadius: 7, border: "none", background: "linear-gradient(135deg, #ef4444, #dc2626)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetUserManagement;
